/**
 * Density-test identifiers: how a test is named on its label, in the map's style — "Test #4"
 * by default, or "#4", "4", "T-4", "Test 4", or YYYYMMDD_INITIALS_N (e.g. 20260909_RB_4). A sand
 * cone test is numbered on its own, beside the nuclear gauge's, and its label says so: "Test #1
 * (Sand Cone)".
 */
import type { Item, TestMethod, TestPoint, TestStyle } from '../../shared/doc.ts';

/** How a test was taken. */
export function methodOf(t: Pick<TestPoint, 'method'>): TestMethod {
  return t.method === 'sandcone' ? 'sandcone' : 'nuke';
}
/** The method as the strip and bar name it. */
export const TEST_METHOD_LABEL: Record<TestMethod, string> = { nuke: 'Nuke', sandcone: 'Sand cone' };
/** What a label adds after the test's name for its method (the nuclear gauge, the usual one, adds nothing). */
export function methodTag(t: Pick<TestPoint, 'method'>): string {
  return methodOf(t) === 'sandcone' ? '(Sand Cone)' : '';
}

export function formatTestId(date: string, initials: string, n: number): string {
  return `${date}_${initials}_${n}`;
}

/** What a test is called on the map: its own name, if the inspector gave it one, else its number. */
export function shownNumber(t: Pick<TestPoint, 'n' | 'name'>): string {
  return t.name ?? String(t.n);
}
/** A test's name in a style (its number — or its own name — alone, or with the date and initials). */
export function testName(t: Pick<TestPoint, 'date' | 'initials' | 'n' | 'name'>, style: TestStyle = 'test'): string {
  const shown = shownNumber(t);
  return style === 'id' ? `${t.date}_${t.initials}_${shown}` : numberName(shown, style);
}
/** Another test, by number, as the style names it ("Retest of Test #4"; the ID style says "Test 4"). */
function numberName(n: number | string, style: TestStyle): string {
  switch (style) {
    case 'test':
      return `Test #${n}`;
    case 'hash':
      return `#${n}`;
    case 'num':
      return String(n);
    case 'dash':
      return `T-${n}`;
    default:
      return `Test ${n}`;
  }
}
/**
 * What a test's badge says, in two parts: the words before the number, in the smaller type ("Test #",
 * "Test "), and the number as the style writes it, in the badge's large type ("4", "#4", "T-4"). A
 * test's own name is shown as typed, all large, with nothing before it; the ID style's badge says
 * "#4" (the whole id is too long for a badge: the legend says what the number stands for).
 */
export function badgeName(t: Pick<TestPoint, 'n' | 'name'>, style: TestStyle = 'test'): { pre: string; n: string } {
  if (t.name) return { pre: '', n: t.name };
  const n = String(t.n);
  switch (style) {
    case 'test':
      return { pre: 'Test #', n };
    case 'plain':
      return { pre: 'Test ', n };
    case 'num':
      return { pre: '', n };
    case 'dash':
      return { pre: '', n: `T-${n}` };
    default:
      return { pre: '', n: `#${n}` };
  }
}
/** Another test as a badge's words call it (a retest's "Retest of …"): its own name as typed, else its number as the badge's style writes it. */
export function badgeRef(name: string | undefined, n: number, style: TestStyle = 'test'): string {
  if (name) return name;
  const b = badgeName({ n }, style);
  return b.pre + b.n;
}
/** The own name of the test numbered `n` (of the same method as `like`), if it was given one. */
export function ownNameOf(items: readonly Item[], n: number, like?: Pick<TestPoint, 'method'>): string | undefined {
  const same = items.filter((it): it is TestPoint => it.t === 'test' && it.n === n && (!like || methodOf(it) === methodOf(like)));
  return same.find((t) => t.name)?.name;
}
/** What the test numbered `n` is called (its own name, when it has one), for a retest to say. */
export function nameOfNumber(items: readonly Item[], n: number, like?: Pick<TestPoint, 'method'>): string {
  return ownNameOf(items, n, like) ?? String(n);
}
/** What each style looks like, for choosing one (with the number of the next test). */
export const TEST_STYLE_LABEL: Record<TestStyle, string> = { test: 'Test #', hash: '#', num: 'Number', dash: 'T-', plain: 'Test', id: 'Date_Initials_#' };

/** Local calendar date as YYYYMMDD. */
export function todayYYYYMMDD(now = new Date()): string {
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}${m}${d}`;
}

export function isoToYYYYMMDD(iso: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[1]}${m[2]}${m[3]}` : null;
}
export function yyyymmddToIso(d: string): string {
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6, 8)}`;
}

/** Human date like "Sep 9, 2026". */
export function formatDateHuman(d: string, locale?: string): string {
  const dt = new Date(Number(d.slice(0, 4)), Number(d.slice(4, 6)) - 1, Number(d.slice(6, 8)));
  return dt.toLocaleDateString(locale, { month: 'short', day: 'numeric', year: 'numeric' });
}

/** The numbers in use by tests of a date and method. */
function usedNumbers(items: readonly Item[], date: string, method: TestMethod): Set<number> {
  const used = new Set<number>();
  for (const it of items) if (it.t === 'test' && it.date === date && methodOf(it) === method) used.add(it.n);
  return used;
}
/**
 * Where a map's numbering of a method starts: where it was set to (`start`), else its lowest number
 * in use, else — no test of the method yet — the number after the day's other maps' (`floor`).
 */
export function numberingStart(items: readonly Item[], date: string, floor = 0, method: TestMethod = 'nuke', start?: number): number {
  if (start !== undefined) return start;
  const used = usedNumbers(items, date, method);
  return used.size ? Math.min(...used) : floor + 1;
}
/**
 * The next test's number for a date and method (each method counts on its own): the lowest number not
 * in use from where the numbering starts (see `numberingStart`) — a test deleted frees its number.
 */
export function nextTestNumber(items: readonly Item[], date: string, floor = 0, method: TestMethod = 'nuke', start?: number): number {
  const used = usedNumbers(items, date, method);
  let n = Math.max(1, numberingStart(items, date, floor, method, start));
  while (used.has(n)) n++;
  return n;
}

/** Status text appended to the id, following the crew's report convention. `of`: what the retested test is called (its own name, if any). */
export function testSuffix(t: Pick<TestPoint, 'status' | 'retestOf' | 'note'>, style: TestStyle = 'id', of: (n: number) => string = String): string {
  const parts: string[] = [];
  if (t.retestOf !== null) {
    parts.push(`[Retest of ${style === 'id' ? `Test ${of(t.retestOf)}` : numberName(of(t.retestOf), style)}${t.status === 'pass' ? ' (Passing)' : t.status === 'fail' ? ' (Failing)' : ''}]`);
  } else if (t.status === 'fail') parts.push('(Failing)');
  else if (t.status === 'pass') parts.push('(Passing)');
  // (on one line, however the note's own lines go)
  if (t.note.trim()) parts.push(t.note.trim().replace(/\s*\n\s*/g, ' '));
  return parts.join(' ');
}

export function testLabelText(t: TestPoint, style: TestStyle = 'test', items: readonly Item[] = []): string {
  return [testName(t, style), methodTag(t), testSuffix(t, style, (n) => nameOfNumber(items, n, t))].filter(Boolean).join(' ');
}

/** Derive initials from a display name or e-mail local part ("ricky.b@x" -> "RB"). */
export function guessInitials(nameOrEmail: string): string {
  const local = nameOrEmail.split('@')[0] ?? '';
  const parts = local.split(/[\s._-]+/).filter(Boolean);
  const letters = parts.map((p) => p[0]!.toUpperCase()).filter((c) => /[A-Z]/.test(c));
  return letters.slice(0, 3).join('') || 'XX';
}
